import { useEffect, useState } from 'react';
import { api, ApiFailure, type SharedReport as Shared } from '../api';
import { Insights, Notice, QualityPanel, Scorecards } from './Panels';

/**
 * The page an anonymous visitor sees behind a share link.
 *
 * No sidebar, no navigation, no session. It carries the workspace's branding
 * because the recipient is usually the agency's client, and it carries a single
 * unobtrusive route back to sign-up — every share is a place someone meets the
 * product for the first time.
 */
export function SharedReportView({ code }: { code: string }) {
  const [data, setData] = useState<Shared | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(true);

  async function load(withPassword?: string): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      setData(await api.viewShare(code, withPassword));
      setNeedsPassword(false);
    } catch (error) {
      const failure =
        error instanceof ApiFailure ? error : new ApiFailure('Could not open this link.', 'Try again.', 0);
      // A 401 here means "protected", not "wrong" — the difference decides
      // whether we show a prompt or an error.
      if (failure.status === 401 && withPassword === undefined) setNeedsPassword(true);
      else setFailure(failure);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const accent = data?.branding?.accentColor ?? undefined;

  if (needsPassword) {
    return (
      <div className="shared-shell">
        <div className="auth-card" style={{ maxWidth: 380 }}>
          <h1 style={{ fontSize: 18 }}>This report is protected</h1>
          <p className="muted" style={{ marginTop: 0 }}>
            Enter the passphrase you were given.
          </p>
          {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void load(password);
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <input
              type="password"
              value={password}
              autoFocus
              onChange={(e) => setPassword(e.target.value)}
            />
            <button className="primary" type="submit" disabled={busy}>
              {busy ? <span className="spinner" /> : 'Open report'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (failure !== null) {
    return (
      <div className="shared-shell">
        <div className="card empty" style={{ maxWidth: 460 }}>
          <h3>{failure.message}</h3>
          <p>{failure.remedy}</p>
          <a href="/">
            <button className="ghost">Go to DataVantage</button>
          </a>
        </div>
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="shared-shell">
        <p className="muted">
          <span className="spinner" /> Opening report…
        </p>
      </div>
    );
  }

  const { report, branding } = data;

  return (
    <div className="shared-page">
      <header className="shared-header" style={accent === undefined ? undefined : { borderTopColor: accent }}>
        <div className="page" style={{ paddingBottom: 0 }}>
          {branding?.logoUrl != null && branding.logoUrl !== '' && (
            <img src={branding.logoUrl} alt="" className="shared-logo" />
          )}
          {branding?.companyName != null && (
            <div className="eyebrow" style={accent === undefined ? undefined : { color: accent }}>
              {branding.companyName.toUpperCase()}
            </div>
          )}
          <h1>{report.title}</h1>
          <p className="muted">
            Generated {new Date(report.createdAt).toLocaleDateString()} · read-only
          </p>
        </div>
      </header>

      <div className="page">
        <Scorecards cards={report.scorecards} />

        {report.narrative !== null && (
          <section className="card narrative">
            <header>
              <h2>Summary</h2>
            </header>
            {report.narrative.summary.split('\n\n').map((paragraph, index) => (
              <p key={index}>{paragraph.replace(/\*\*(.+?)\*\*/g, '$1')}</p>
            ))}
            <div className="provenance">
              {report.narrative.degraded
                ? 'Written offline — the configured AI provider was unavailable. The figures are unaffected.'
                : report.narrative.provider === 'deterministic'
                  ? 'Composed directly from the computed findings.'
                  : `Written by ${report.narrative.provider}. Figures were computed by the analytics core, not by the model.`}
            </div>
          </section>
        )}

        <section className="grid">
          <Insights insights={report.insights} />
          <QualityPanel quality={report.quality} />
        </section>

        {/* Charts are listed rather than drawn: rendering them would need query
            access to the dataset, which a share link deliberately does not have. */}
        {report.charts.length > 0 && (
          <section className="card">
            <header>
              <h2>Recommended views</h2>
              <span className="sub">available in the full report</span>
            </header>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {report.charts.slice(0, 6).map((chart) => (
                <li key={chart.id} style={{ fontSize: 13.5, marginBottom: 4 }}>
                  <strong>{chart.title}</strong>{' '}
                  <span className="muted">— {chart.rationale}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {(branding?.showPoweredBy ?? true) && (
          <p className="muted" style={{ textAlign: 'center', marginTop: 8 }}>
            Made with{' '}
            <a href="/" style={{ color: 'var(--series-1)' }}>
              DataVantage
            </a>{' '}
            — every figure computed from the source data.
          </p>
        )}
      </div>
    </div>
  );
}
